import { FC } from 'react';
import clsx from 'clsx';
import { toRem } from '@marriott/mi-ui-library';
import {
  BASELINE_SEARCH_FORM_HEIGHTS,
  NGSF_CONTAINER_GUTTERS,
  NGSF_CONTAINER_MAX_WIDTHS,
  SkeletonLoaderBreakpoint,
  StyledSkeletonLoaderDiv,
  StyledSkeletonLoaderFormContainer,
} from './SearchFormSkeletonLoader.styles';

interface SearchFormSkeletonLoaderProps {
  enableOverlayTop: boolean;
}

/** Convert an NGSF container max-width entry to a CSS length. */
const ngsfMaxWidthToCss = (bp: SkeletonLoaderBreakpoint): string => {
  const value = NGSF_CONTAINER_MAX_WIDTHS[bp];
  return value === 'none' ? '100%' : toRem(value);
};

/**
 * Skeleton loader rendered while the AB test target call is in flight.
 *
 * Position parity with `StickyComponentWrapper`:
 * - Top offsets are CSS-driven via `body.polaris-page` (set in homepage
 *   `_document.tsx`) so Polaris vs BAU is correct on SSR/first paint without
 *   waiting for client-side model hydration.
 * - When `enableOverlayTop` is true the skeleton is absolutely positioned over
 *   the herobanner.
 *
 * Dimensions:
 * - Height per breakpoint mirrors the baseline SearchForm pill
 *   (`BASELINE_SEARCH_FORM_HEIGHTS`).
 * - Width per breakpoint mirrors the Next-Gen SearchForm container
 *   (`NGSF_CONTAINER_MAX_WIDTHS`).
 */
export const SearchFormSkeletonLoader: FC<SearchFormSkeletonLoaderProps> = ({ enableOverlayTop }) => {
  return (
    <StyledSkeletonLoaderFormContainer
      className={clsx({ 'search-form-position': enableOverlayTop })}
      $heightXs={toRem(BASELINE_SEARCH_FORM_HEIGHTS.xs)}
      $heightSm={toRem(BASELINE_SEARCH_FORM_HEIGHTS.sm)}
      $heightMd={toRem(BASELINE_SEARCH_FORM_HEIGHTS.md)}
      $heightLg={toRem(BASELINE_SEARCH_FORM_HEIGHTS.lg)}
      $maxWidthXs={ngsfMaxWidthToCss('xs')}
      $maxWidthSm={ngsfMaxWidthToCss('sm')}
      $maxWidthMd={ngsfMaxWidthToCss('md')}
      $maxWidthLg={ngsfMaxWidthToCss('lg')}
      $gutterXs={toRem(NGSF_CONTAINER_GUTTERS.xs)}
      $gutterSm={toRem(NGSF_CONTAINER_GUTTERS.sm)}
      $gutterMd={toRem(NGSF_CONTAINER_GUTTERS.md)}
      $gutterLg={toRem(NGSF_CONTAINER_GUTTERS.lg)}
    >
      <StyledSkeletonLoaderDiv className="search-form-wrapper">
        <div className="search-form-skeleton">
          <img
            src="https://cache.marriott.com/content/dam/marriott-digital/cy/us-canada/hws/h/hpnnb/en_us/logo/internal/assets/cy-hpnnb-grey-image-placeholder-34740.png?imbypass=on"
            alt="loading"
            className="skeleton-image"
          />
        </div>
      </StyledSkeletonLoaderDiv>
    </StyledSkeletonLoaderFormContainer>
  );
};
